import { useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import { DEFAULT_COPY, DEFAULT_HERO, DEFAULT_HOW_IT_WORKS, DEFAULT_LEAD_FORM, PORTFOLIO_ROWS, PRODUCTS, QUESTIONS } from "@/data/polyFitData";

const toCamel = (item = {}) => ({
  ...item,
  brandName: item.brandName || item.brand_name,
  imageUrl: item.imageUrl || item.image_url,
  bestFor: item.bestFor || item.best_for,
  alsoConsider: item.alsoConsider || item.also_consider,
  portfolioBestFit: item.portfolioBestFit || item.portfolio_best_fit,
  noiseFocusStepUp: item.noiseFocusStepUp || item.noise_focus_step_up,
  titleAccent: item.titleAccent || item.title_accent,
  ctaLabel: item.ctaLabel || item.cta_label,
  backgroundImageUrl: item.backgroundImageUrl || item.background_image_url,
  bannerImageUrl: item.bannerImageUrl || item.banner_image_url,
  missionImageUrl: item.missionImageUrl || item.mission_image_url,
  focusImageUrl: item.focusImageUrl || item.focus_image_url,
  specText: item.specText || item.spec_text,
  buttonLabel: item.buttonLabel || item.button_label,
  seriesNote: item.seriesNote || item.series_note,
  titleTemplate: item.titleTemplate || item.title_template,
  submitLabel: item.submitLabel || item.submit_label,
  successMessage: item.successMessage || item.success_message,
  contactMethods: item.contactMethods || item.contact_methods,
  hotspots: item.hotspots ? { imageUrl: item.hotspots.imageUrl || item.hotspots.image_url, points: (item.hotspots.points || []).map((pt) => ({ ...pt, imageUrl: pt.imageUrl || pt.image_url })) } : undefined,
});

const normalizeOption = (option = {}) => ({
  ...option,
  nextQuestionId: option.nextQuestionId || option.next_question_id,
  resultProductId: option.resultProductId || option.result_product_id,
  completedSteps: option.completedSteps || option.completed_steps,
  imageUrl: option.imageUrl || option.image_url,
});

const normalizeQuestion = (question = {}) => ({
  ...question,
  progressLabel: question.progressLabel || question.progress_label,
  multiSelect: Boolean(question.multiSelect ?? question.multi_select),
  branchStep: question.branchStep || question.branch_step,
  branchTotal: question.branchTotal || question.branch_total,
  options: (question.options || []).map(normalizeOption),
});

const normalizePortfolioRow = (row = {}) => ({
  ...row,
  bestFitWorkplace: row.bestFitWorkplace || row.best_fit_workplace,
  noiseFocusStepUp: row.noiseFocusStepUp || row.noise_focus_step_up,
});

export const fallbackAdvisorContent = {
  hero: DEFAULT_HERO,
  howItWorks: DEFAULT_HOW_IT_WORKS,
  questions: QUESTIONS,
  products: PRODUCTS,
  portfolioRows: PORTFOLIO_ROWS,
  leadForm: DEFAULT_LEAD_FORM,
  copy: DEFAULT_COPY,
};

export const mergeCopy = (stored = {}) => Object.fromEntries(Object.entries(DEFAULT_COPY).map(([section, defaults]) => [section, { ...defaults, ...(stored?.[section] || {}) }]));
export const fillTemplate = (text = "", values = {}) => text.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? "");

export function normalizeAdvisorConfig(config = {}) {
  const questions = Object.fromEntries((config.questions || []).map((item) => {
    const question = normalizeQuestion(item);
    return [question.id, question];
  }));
  const products = Object.fromEntries((config.products || []).map((item) => {
    const product = toCamel(item);
    return [product.id, product];
  }));
  return {
    hero: { ...DEFAULT_HERO, ...toCamel(config.hero || {}) },
    howItWorks: { ...DEFAULT_HOW_IT_WORKS, ...toCamel(config.how_it_works || {}), steps: config.how_it_works?.steps || DEFAULT_HOW_IT_WORKS.steps },
    questions: Object.keys(questions).length ? questions : QUESTIONS,
    products: Object.keys(products).length ? products : PRODUCTS,
    portfolioRows: (config.portfolio_rows || PORTFOLIO_ROWS).map(normalizePortfolioRow),
    leadForm: { ...DEFAULT_LEAD_FORM, ...toCamel(config.lead_form || {}), labels: { ...DEFAULT_LEAD_FORM.labels, ...(config.lead_form?.labels || {}) } },
    copy: mergeCopy(config.copy),
    rawContent: config.content,
  };
}

export const isPreviewMode = () => { try { return window.self !== window.top; } catch { return true; } };

let previewContent = null;
const previewListeners = new Set();
const requestPreviewContent = () => { try { if (isPreviewMode()) window.parent.postMessage({ type: "poly-fit-preview-ready" }, window.location.origin); } catch { /* cross-origin parent */ } };
if (typeof window !== "undefined") {
  window.addEventListener("message", (event) => {
    if (event.origin !== window.location.origin || event.data?.type !== "poly-fit-preview") return;
    previewContent = normalizeAdvisorConfig(event.data.content || {});
    previewListeners.forEach((listener) => listener(previewContent));
  });
  requestPreviewContent();
}

export function useAdvisorContent() {
  const [content, setContent] = useState(previewContent || fallbackAdvisorContent);
  useEffect(() => {
    previewListeners.add(setContent);
    if (previewContent) setContent(previewContent);
    else {
      requestPreviewContent();
      api.getAdvisorConfig().then((data) => { if (!previewContent) setContent(normalizeAdvisorConfig(data)); }).catch(() => setContent(fallbackAdvisorContent));
    }
    return () => previewListeners.delete(setContent);
  }, []);
  return content;
}

export function useContestConfig() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const loadConfig = useCallback(() => {
    return api.getConfig().then(setConfig).catch(() => setError("Could not load contest settings."));
  }, [setConfig, setError]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);
  return { config, error };
}

export function useSession(sessionId) {
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const refresh = useCallback(() => api.getSession(sessionId).then(setSession).catch(() => setError("Session not found.")), [sessionId, setError, setSession]);
  useEffect(() => { if (sessionId) refresh(); }, [sessionId, refresh]);
  return { session, error, refresh };
}